import { useEffect, useRef, useState } from 'react'
import type { Citation, TranscriptMessage } from '~/api/types'
import { Badge } from '~/components/ui/badge'
import { cn, formatDateTime } from '~/lib/utils'

// 재생/상세에서 공유하는 대화 transcript 렌더. role별 말풍선 + 가독형 참조정보(citation) 리스트.

/** 메시지 리스트를 말풍선으로 렌더(user 우측/assistant 좌측).
 *  highlightId가 있으면 해당 메시지를 강조하고 화면에 스크롤한다(피드백 드릴다운). */
export function MessageList({
    messages,
    highlightId,
}: {
    messages: TranscriptMessage[]
    highlightId?: string
}) {
    return (
        <div className='space-y-3'>
            {messages.map((m) => (
                <MessageBubble
                    key={m.id}
                    message={m}
                    highlight={m.id === highlightId}
                />
            ))}
        </div>
    )
}

export function MessageBubble({
    message,
    highlight = false,
}: {
    message: TranscriptMessage
    highlight?: boolean
}) {
    const isUser = message.role === 'user'
    const ref = useRef<HTMLDivElement>(null)

    // 강조 대상은 마운트 후 화면 중앙으로 스크롤한다(평가된 답변으로 이동).
    useEffect(() => {
        if (highlight && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [highlight])

    return (
        <div
            ref={ref}
            className={cn(
                'flex',
                isUser ? 'justify-end' : 'justify-start',
                highlight && 'ring-primary/60 rounded-lg p-1 ring-2'
            )}>
            <div className='max-w-[80%] min-w-0'>
                <div
                    className={cn(
                        'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                        isUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground'
                    )}>
                    {message.content}
                </div>
                <CitationList citations={message.citations} />
                <div
                    className={cn(
                        'text-muted-foreground mt-1 text-[11px]',
                        isUser ? 'text-right' : 'text-left'
                    )}>
                    {message.role} · {formatDateTime(message.createdAt)}
                </div>
            </div>
        </div>
    )
}

/** 참조정보를 세로 리스트로 — 겹치지 않게 flex-col. 항목 클릭 시 snippet 전문 펼침. */
export function CitationList({ citations }: { citations: unknown[] }) {
    if (!citations || citations.length === 0) return null
    return (
        <div className='border-border mt-2 flex flex-col gap-1.5 rounded-[var(--radius)] border p-2'>
            <div className='text-muted-foreground text-[11px] font-medium'>
                참조정보 {citations.length}건
            </div>
            {citations.map((c, i) => (
                <CitationItem
                    key={i}
                    citation={normalizeCitation(c)}
                    index={i}
                />
            ))}
        </div>
    )
}

function CitationItem({
    citation,
    index,
}: {
    citation: Citation
    index: number
}) {
    const [open, setOpen] = useState(false)
    const clause = citation.clause?.trim()
    const title = citation.title?.trim() || `출처 ${index + 1}`
    const snippet = citation.snippet?.trim()
    const hasSnippet = Boolean(snippet)

    return (
        <div className='flex flex-col gap-1'>
            <button
                type='button'
                onClick={() => hasSnippet && setOpen((v) => !v)}
                aria-expanded={hasSnippet ? open : undefined}
                disabled={!hasSnippet}
                className={cn(
                    'flex items-center gap-1.5 rounded-[var(--radius-sm)] px-1 py-0.5 text-left',
                    hasSnippet && 'hover:bg-secondary cursor-pointer'
                )}>
                {clause ? (
                    <Badge tone='info' className='shrink-0'>
                        {clause}
                    </Badge>
                ) : null}
                <span className='text-foreground min-w-0 flex-1 truncate text-[12px]'>
                    {title}
                </span>
                {typeof citation.score === 'number' ? (
                    <span className='text-muted-foreground shrink-0 text-[11px] tabular-nums'>
                        {citation.score.toFixed(2)}
                    </span>
                ) : null}
                {hasSnippet ? (
                    <span className='text-muted-foreground shrink-0 text-[11px]'>
                        {open ? '접기' : '펼치기'}
                    </span>
                ) : null}
            </button>
            {open && snippet ? (
                <p className='text-muted-foreground border-border bg-muted/40 ml-1 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[12px] whitespace-pre-wrap'>
                    {snippet}
                </p>
            ) : null}
        </div>
    )
}

/** citation 원본(객체/문자열)을 표시 모델로 정규화한다(계약 미확정 입력 방어). */
function normalizeCitation(value: unknown): Citation {
    if (typeof value === 'string') return { title: value }
    if (value && typeof value === 'object') {
        const rec = value as Record<string, unknown>
        return {
            title: asText(rec.title ?? rec.source ?? rec.label),
            clause: asText(rec.clause),
            snippet: asText(rec.snippet),
            score: typeof rec.score === 'number' ? rec.score : null,
            documentId: asText(rec.documentId),
            page: typeof rec.page === 'number' ? rec.page : null,
        }
    }
    return {}
}

function asText(value: unknown): string | null {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return null
}
